import talentDashboardData from './talentDashboardData.json';
import swisData from './swisData.json';
import dosmData from './dosmData.json';

/**
 * Build the MEITD-specific system prompt and dataset context for the AI assistant.
 */
export function generateMEITDMessages(questionPrompt) {
    // Compact key data for the AI context
    const swisContext = {
        source: swisData.source,
        overview: swisData.overview,
        institutionSupply: {
            graduates: { total: swisData.institutionSupply.graduates.total, years: swisData.institutionSupply.graduates.years, data: swisData.institutionSupply.graduates.data, reporting: `${swisData.institutionSupply.graduates.reportingInstitutions} of ${swisData.institutionSupply.graduates.totalInstitutions}` },
            enrollment: { total: swisData.institutionSupply.enrollment.total, data: swisData.institutionSupply.enrollment.data },
            courseCapacity: { total: swisData.institutionSupply.courseCapacity.total, data: swisData.institutionSupply.courseCapacity.data },
        },
        industrialDemand: {
            employment: { total: swisData.industrialDemand.employment.total, data: swisData.industrialDemand.employment.data, reporting: `${swisData.industrialDemand.employment.reportingCompanies} of ${swisData.industrialDemand.employment.totalCompanies}` },
            vacancies: { total: swisData.industrialDemand.vacancies.total, data: swisData.industrialDemand.vacancies.data },
            hiresFilled: { total: swisData.industrialDemand.hiresFilled.total, data: swisData.industrialDemand.hiresFilled.data },
        },
        tracerStudy2024: swisData.tracerStudyOutcomes2024,
        courseCategories: swisData.courseCategories,
    };

    const dosmContext = {
        source: dosmData.source,
        state: dosmData.state,
        year: dosmData.year,
        overview: dosmData.overview,
        nationalComparison: dosmData.nationalComparison,
        stateRanking: dosmData.stateRanking,
        lfprByAge: dosmData.lfprByAge,
        labourForceByEducation: dosmData.labourForceByEducation,
        labourForceByCertificate: dosmData.labourForceByCertificate,
        employmentToPopulationRatio: dosmData.employmentToPopulationRatio,
        skillRelatedUnderemployment: dosmData.skillRelatedUnderemployment,
        yearOverYear: dosmData.yearOverYear,
        youthData: dosmData.youthData,
    };

    const talentContext = {
        summary: talentDashboardData.summary,
        supplyDemand: talentDashboardData.supplyDemand,
        skillsGapAnalysis: talentDashboardData.skillsGapAnalysis,
        skillsDemand: talentDashboardData.skillsDemand,
        industryReadiness: talentDashboardData.industryReadiness,
        employmentBySector: talentDashboardData.employmentBySector,
        employmentRate: talentDashboardData.employmentRate,
        divisionSnapshot: talentDashboardData.divisionSnapshot,
        factors: talentDashboardData.factors,
    };

    return [
        {
            role: 'system',
            content: `You are the MEITD Talent Development AI Assistant — a senior workforce strategist and data analyst specializing in Sarawak's talent ecosystem. You serve the Ministry of Education, Innovation and Talent Development (MEITD) of Sarawak.

Your expertise covers:
1. SWIS (Sarawak Workforce Information System) — institution supply, industrial demand, graduate tracer studies
2. DOSM Labour Force Survey data — Sarawak's LFPR, employment ratios, education levels, youth workforce participation
3. PCDS 2030 (Post-COVID Development Strategy) — industry readiness across 15 key sectors
4. Skills gap analysis — supply vs demand for critical skills
5. Workforce forecasting — supply and demand predictions through 2030

Your communication style:
- Professional, data-driven, and concise
- Always cite specific numbers from the provided datasets
- Calculate percentages, gaps, and trends when relevant
- Frame insights in terms of policy implications for Sarawak
- Use clear headers and bullet points for readability
- When comparing, show actual figures side-by-side
- Highlight key takeaways and actionable recommendations

You MUST only use the provided datasets for quantitative insights. Never fabricate statistics.`,
        },
        {
            role: 'user',
            content: `Here is the SWIS (Sarawak Workforce Information System) dataset:\n${JSON.stringify(swisContext)}`,
        },
        {
            role: 'user',
            content: `Here is the DOSM Labour Force Survey 2024 dataset for Sarawak:\n${JSON.stringify(dosmContext)}`,
        },
        {
            role: 'user',
            content: `Here is the MEITD Talent Development Dashboard dataset including skills gaps, industry readiness (PCDS 2030), employment trends, and division snapshots:\n${JSON.stringify(talentContext)}`,
        },
        {
            role: 'system',
            content: `When answering questions, always:
1. Reference specific data points from the datasets provided
2. Calculate and show percentages, gaps, or trends where applicable
3. Provide strategic recommendations aligned with PCDS 2030
4. Use markdown formatting: **bold** for emphasis, headers with ###, bullet points with -
5. Keep responses focused and structured — typically 200-400 words unless a detailed analysis is requested`,
        },
        { role: 'user', content: questionPrompt },
    ];
}

/**
 * Get random suggested questions for the MEITD dashboard AI assistant.
 */
export function getMEITDSuggestedQuestions() {
    const questionsPool = [
        'What is the current skills gap in Sarawak and which skills need the most urgent attention?',
        'How does Sarawak\'s LFPR compare to the national average and what does this mean for workforce development?',
        'What are the key findings from the SWIS tracer study on graduate employment outcomes?',
        'Which PCDS 2030 sectors have the largest readiness gaps and what should be prioritised?',
        'Analyse the supply vs demand trend from 2019 to 2025 — what patterns emerge?',
        'What is the gender gap in Sarawak\'s workforce participation and how does it vary by age group?',
        'How has the skill-related underemployment rate changed from 2023 to 2024?',
        'Which divisions in Sarawak have the lowest workforce readiness and why?',
        'What percentage of graduates find permanent employment based on the tracer study?',
        'What are the top 3 factors affecting Sarawak\'s talent development metrics?',
        'Compare Sarawak vs Malaysia on unemployment rate and employment-to-population ratio.',
        'What is the education profile of Sarawak\'s labour force and what does it suggest for policy?',
        'How many vacancies remain unfilled in SWIS and what is the vacancy fill rate trend?',
        'What workforce forecasts does the data show for 2026-2030?',
        'What are the youth unemployment challenges in Sarawak based on DOSM data?',
    ];

    return questionsPool.sort(() => 0.5 - Math.random()).slice(0, 3);
}
